/**
 * Exact Jalali (Persian) ↔ Gregorian conversion.
 *
 * Port of the Birashk/Borkowski algorithm used by `jalaali-js`. `Intl` can only
 * format a Jalali date, not build a month grid, so the calendar needs a real
 * two-way conversion.
 */

export interface JalaliDate {
  jy: number
  jm: number
  jd: number
}

export const JALALI_MONTHS = [
  'فروردین',
  'اردیبهشت',
  'خرداد',
  'تیر',
  'مرداد',
  'شهریور',
  'مهر',
  'آبان',
  'آذر',
  'دی',
  'بهمن',
  'اسفند',
]

/** Saturday-first, matching the rest of the booking module. */
export const JALALI_WEEKDAY_SHORT = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج']

const BREAKS = [
  -61, 9, 38, 199, 426, 686, 756, 818, 1111, 1181, 1210,
  1635, 2060, 2097, 2192, 2262, 2324, 2394, 2456, 3178,
]

function div(a: number, b: number) {
  return Math.trunc(a / b)
}

function mod(a: number, b: number) {
  return a - Math.trunc(a / b) * b
}

function jalCal(jy: number) {
  const gy = jy + 621
  let leapJ = -14
  let jp = BREAKS[0]!
  let jump = 0

  for (let i = 1; i < BREAKS.length; i++) {
    const jm = BREAKS[i]!
    jump = jm - jp
    if (jy < jm) break
    leapJ += div(jump, 33) * 8 + div(mod(jump, 33), 4)
    jp = jm
  }

  let n = jy - jp
  leapJ += div(n, 33) * 8 + div(mod(n, 33) + 3, 4)
  if (mod(jump, 33) === 4 && jump - n === 4) leapJ += 1

  const leapG = div(gy, 4) - div((div(gy, 100) + 1) * 3, 4) - 150
  const march = 20 + leapJ - leapG

  if (jump - n < 6) n = n - jump + div(jump + 4, 33) * 33
  let leap = mod(mod(n + 1, 33) - 1, 4)
  if (leap === -1) leap = 4

  return { leap, gy, march }
}

function g2d(gy: number, gm: number, gd: number) {
  let d = div((gy + div(gm - 8, 6) + 100100) * 1461, 4)
    + div(153 * mod(gm + 9, 12) + 2, 5)
    + gd - 34840408
  d = d - div(div(gy + 100100 + div(gm - 8, 6), 100) * 3, 4) + 752
  return d
}

function d2g(jdn: number) {
  let j = 4 * jdn + 139361631
  j += div(div(4 * jdn + 183187720, 146097) * 3, 4) * 4 - 3908
  const i = div(mod(j, 1461), 4) * 5 + 308
  const gd = div(mod(i, 153), 5) + 1
  const gm = mod(div(i, 153), 12) + 1
  const gy = div(j, 1461) - 100100 + div(8 - gm, 6)
  return { gy, gm, gd }
}

function j2d(jy: number, jm: number, jd: number) {
  const r = jalCal(jy)
  return g2d(r.gy, 3, r.march) + (jm - 1) * 31 - div(jm, 7) * (jm - 7) + jd - 1
}

function d2j(jdn: number): JalaliDate {
  const gy = d2g(jdn).gy
  let jy = gy - 621
  const r = jalCal(jy)
  let k = jdn - g2d(gy, 3, r.march)

  if (k >= 0) {
    if (k <= 185) return { jy, jm: 1 + div(k, 31), jd: mod(k, 31) + 1 }
    k -= 186
  }
  else {
    jy -= 1
    k += 179
    if (r.leap === 1) k += 1
  }

  return { jy, jm: 7 + div(k, 30), jd: mod(k, 30) + 1 }
}

export function isLeapJalaliYear(jy: number) {
  return jalCal(jy).leap === 0
}

export function jalaliMonthLength(jy: number, jm: number) {
  if (jm <= 6) return 31
  if (jm <= 11) return 30
  return isLeapJalaliYear(jy) ? 30 : 29
}

/** `YYYY-MM-DD` (Gregorian) → Jalali parts. */
export function isoToJalali(iso: string): JalaliDate {
  const [gy, gm, gd] = iso.split('-').map(Number) as [number, number, number]
  return d2j(g2d(gy, gm, gd))
}

/** Jalali parts → `YYYY-MM-DD` (Gregorian). */
export function jalaliToIso(jy: number, jm: number, jd: number) {
  const { gy, gm, gd } = d2g(j2d(jy, jm, jd))
  return `${String(gy).padStart(4, '0')}-${String(gm).padStart(2, '0')}-${String(gd).padStart(2, '0')}`
}

/** 0 = Saturday … 6 = Friday, matching `WEEKDAY_LABELS`. */
export function jalaliWeekday(jy: number, jm: number, jd: number) {
  const { gy, gm, gd } = d2g(j2d(jy, jm, jd))
  return (Date.UTC(gy, gm - 1, gd) / 86_400_000 + 5) % 7
}

export function addJalaliMonths(jy: number, jm: number, delta: number) {
  const total = (jy * 12 + (jm - 1)) + delta
  return { jy: Math.floor(total / 12), jm: (total % 12) + 1 }
}

export function formatJalali(iso: string) {
  const { jy, jm, jd } = isoToJalali(iso)
  return `${jd} ${JALALI_MONTHS[jm - 1]} ${jy}`
}
